import type { ComponentPropsWithoutRef } from "react";

import { CommandMan, CommandType, State, type Fan as FanType } from "@eisox/api/core-api";
import { cn } from "@eisox/design-system";

import WarningIcon from "~/assets/boilerroomWarning.svg?react";
import Commutator from "~/assets/commutator.svg?react";
import { Tooltip } from "~/components/tooltip";
import { m } from "~/paraglide/messages";

import { getActuatorState, getSpeedLabel, isAutoManualOrForcing } from "../helpers";
import { Direction, Position, type Component } from "../types";
import { Card } from "./card";
import { PressureSwitch } from "./pressure-switch";

interface FanProps extends Omit<FanType, "id">, Component, ComponentPropsWithoutRef<"div"> {}

const Fan: React.FC<FanProps> = ({
  state: stateProp,
  command,
  commandMan,
  commandAutomate,
  commandType,
  defect = false,
  missmatchDefect = false,
  pressureSwitchDefect,
  position = Position.TOP,
  direction = Direction.RIGHT,
  ...props
}) => {
  const { state, warning } = getActuatorState({ state: stateProp, commandMan, commandAutomate, defect });
  const autoManualOrForcing = isAutoManualOrForcing(commandMan, command);

  const getSpeed = () => {
    switch (commandType) {
      case CommandType.PERCENTAGE:
        return state;
      case CommandType.ALL_OR_NOTHING:
        return state === State.ON ? 100 : 0;
      case CommandType.TWO_SPEED:
        if (state === 0) return 0;
        if (state === 1) return 50;
        if (state === 2) return 100;
        return 0;
      case CommandType.THREE_SPEED:
        if (state === 0) return 0;
        if (state === 1) return 33;
        if (state === 2) return 66;
        if (state === 3) return 100;
        return 0;
      default:
        return 0;
    }
  };

  const speed = getSpeed();

  const duration = speed && speed > 0 ? 50 / speed : 0;

  return (
    <div
      className={cn(
        "gap-1.25 [&_svg]:mx-2.5 flex w-fit flex-col items-center",
        position === Position.BOTTOM && "flex-col-reverse",
      )}
      {...props}
      style={{ cursor: props.onClick ? "pointer" : "default" }}
    >
      <Card defect={defect || missmatchDefect}>
        {commandMan === CommandMan.MANUAL && <Commutator />}
        {commandType === CommandType.PERCENTAGE &&
          `${m.actuator_state({ state: autoManualOrForcing })} - ${speed?.toFixed(1) ?? "--"} %`}
        {commandType === CommandType.ALL_OR_NOTHING &&
          `${m.actuator_state({ state: autoManualOrForcing })} - ${state !== null ? (state === State.ON ? m.on() : m.off()) : "--"}`}
        {commandType === CommandType.TWO_SPEED &&
          `${m.actuator_state({ state: autoManualOrForcing })} - ${state !== null ? getSpeedLabel(CommandType.TWO_SPEED, state) : "--"}`}
        {commandType === CommandType.THREE_SPEED &&
          `${m.actuator_state({ state: autoManualOrForcing })} - ${state !== null ? getSpeedLabel(CommandType.THREE_SPEED, state) : "--"}`}
        {warning && (
          <Tooltip content={warning}>
            <WarningIcon />
          </Tooltip>
        )}
      </Card>
      {pressureSwitchDefect !== undefined && <PressureSwitch position={position} defect={pressureSwitchDefect} />}
      {direction === Direction.RIGHT && <RightMotor duration={duration} />}
      {direction == Direction.LEFT && <LeftMotor duration={duration} />}
    </div>
  );
};

const RightMotor: React.FC<{ duration: number }> = ({ duration }) => (
  <svg width="106" height="92" viewBox="0 0 106 92" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M40.2954 0.5H100.994L101.192 0.504883C102.181 0.556305 103.119 0.974042 103.823 1.68164C104.574 2.43657 104.997 3.46058 105 4.5293V34.0127C104.997 35.081 104.574 36.1047 103.823 36.8594C103.072 37.6141 102.054 38.0385 100.994 38.041H79.5308V39.999C79.5177 50.4615 75.3807 60.4925 68.0269 67.8955C60.6729 75.2985 50.7003 79.4701 40.2935 79.499C29.8731 79.4986 19.8794 75.3385 12.5103 67.9316C5.14064 60.5241 0.999512 50.4769 0.999512 40C0.999512 29.5231 5.14064 19.4759 12.5103 12.0684C19.8798 4.6611 29.8745 0.5 40.2954 0.5Z"
      fill="#F3F3F9"
      stroke="#122138"
    />
    <path
      d="M4.99951 72.5H101C103.185 72.5 105 74.3729 105 76.7373V87.2627C105 89.6271 103.185 91.5 101 91.5H4.99951C2.81423 91.5 0.999512 89.6271 0.999512 87.2627V76.7373C0.999512 74.3729 2.81423 72.5 4.99951 72.5Z"
      fill="#F3F3F9"
      stroke="#122138"
    />
    <g>
      <animateTransform
        attributeName="transform"
        type="rotate"
        values="0 39.81 40.25;360 39.81 40.25"
        dur={duration > 0 ? `${duration}s` : "0s"}
        repeatCount="indefinite"
      />
      <path
        d="M39.8098 14.5098C34.7189 14.5098 29.7424 16.0194 25.5095 18.8477C21.2765 21.6761 17.9774 25.6961 16.0292 30.3995C14.081 35.1029 13.5712 40.2783 14.5644 45.2714C15.5576 50.2645 18.0091 54.8509 21.6089 58.4507C25.2087 62.0505 29.7951 64.502 34.7882 65.4952C39.7813 66.4884 44.9567 65.9786 49.6601 64.0304C54.3635 62.0822 58.3835 58.7831 61.2119 54.5501C64.0402 50.3172 65.5498 45.3407 65.5498 40.2498C65.5512 36.8692 64.8863 33.5215 63.5932 30.3979C62.3001 27.2744 60.4041 24.4363 58.0137 22.0459C55.6233 19.6555 52.7852 17.7595 49.6617 16.4664C46.5382 15.1733 43.1904 14.5085 39.8098 14.5098Z"
        fill="#F9F9F9"
      />
      <path
        d="M39.8098 15.5098C44.7029 15.5098 49.4862 16.9607 53.5546 19.6792C57.6231 22.3977 60.7941 26.2615 62.6666 30.7822C64.5391 35.3028 65.0291 40.2772 64.0745 45.0763C63.1199 49.8754 60.7636 54.2836 57.3037 57.7436C53.8437 61.2035 49.4355 63.5598 44.6364 64.5144C39.8373 65.469 34.8629 64.9791 30.3422 63.1065C25.8216 61.234 21.9577 58.063 19.2393 53.9946C16.5208 49.9261 15.0698 45.1429 15.0698 40.2498C15.0778 33.6907 17.6869 27.4026 22.3248 22.7647C26.9627 18.1268 33.2508 15.5177 39.8098 15.5098ZM39.8098 14.5098C34.7189 14.5098 29.7424 16.0194 25.5095 18.8477C21.2765 21.6761 17.9774 25.6961 16.0292 30.3995C14.081 35.1029 13.5712 40.2783 14.5644 45.2714C15.5576 50.2645 18.0091 54.8509 21.6089 58.4507C25.2087 62.0505 29.7951 64.502 34.7882 65.4952C39.7813 66.4884 44.9567 65.9786 49.6601 64.0304C54.3635 62.0822 58.3835 58.7831 61.2119 54.5501C64.0402 50.3172 65.5498 45.3407 65.5498 40.2498C65.5512 36.8692 64.8863 33.5215 63.5932 30.3979C62.3001 27.2744 60.4041 24.4363 58.0137 22.0459C55.6233 19.6555 52.7852 17.7595 49.6617 16.4664C46.5382 15.1733 43.1904 14.5085 39.8098 14.5098Z"
        fill="#122138"
      />
      <path
        d="M39.8087 33.0698C38.3882 33.0679 36.999 33.4873 35.8169 34.275C34.6349 35.0628 33.713 36.1834 33.1681 37.4952C32.6231 38.807 32.4795 40.251 32.7554 41.6444C33.0313 43.0379 33.7144 44.3182 34.7181 45.3233C35.7218 46.3285 37.0012 47.0133 38.3942 47.2911C39.7873 47.569 41.2315 47.4274 42.544 46.8842C43.8566 46.3411 44.9785 45.4208 45.7679 44.2399C46.5573 43.0589 46.9787 41.6703 46.9787 40.2498C46.9787 38.3473 46.2236 36.5226 44.8792 35.1763C43.5349 33.8301 41.7112 33.0725 39.8087 33.0698Z"
        fill="#F3F3F9"
      />
      <path
        d="M39.8087 34.0698C41.0309 34.0698 42.2258 34.4323 43.2421 35.1114C44.2584 35.7904 45.0505 36.7556 45.5182 37.8848C45.986 39.0141 46.1084 40.2567 45.8699 41.4555C45.6315 42.6543 45.0429 43.7555 44.1786 44.6198C43.3143 45.484 42.2131 46.0726 41.0143 46.3111C39.8155 46.5495 38.5729 46.4272 37.4437 45.9594C36.3144 45.4917 35.3492 44.6996 34.6702 43.6833C33.9911 42.667 33.6287 41.4721 33.6287 40.2498C33.6287 39.4383 33.7885 38.6346 34.0991 37.8848C34.4097 37.1351 34.8649 36.4538 35.4387 35.8799C36.0126 35.306 36.6939 34.8508 37.4437 34.5403C38.1935 34.2297 38.9971 34.0698 39.8087 34.0698ZM39.8087 33.0698C38.3882 33.0679 36.999 33.4873 35.8169 34.275C34.6349 35.0628 33.713 36.1834 33.1681 37.4952C32.6231 38.807 32.4795 40.251 32.7554 41.6444C33.0313 43.0379 33.7144 44.3182 34.7181 45.3233C35.7218 46.3285 37.0012 47.0133 38.3942 47.2911C39.7873 47.569 41.2315 47.4274 42.544 46.8842C43.8566 46.3411 44.9785 45.4208 45.7679 44.2399C46.5573 43.0589 46.9787 41.6703 46.9787 40.2498C46.9787 38.3473 46.2236 36.5226 44.8792 35.1763C43.5349 33.8301 41.7112 33.0725 39.8087 33.0698Z"
        fill="#122138"
      />
      <path
        d="M45.7095 24.2271C49.579 22.4339 54.8589 22.5055 61.1841 25.8687L60.7153 26.7516C54.5909 23.495 49.6458 23.5054 46.1304 25.1344C42.6048 26.7682 40.4264 30.069 39.9038 33.5738L39.4097 33.4996L38.9155 33.4264C39.4829 29.6212 41.85 26.0158 45.7095 24.2271Z"
        fill="#122138"
      />
      <path
        d="M44.0054 34.7853C47.1255 32.5129 51.3514 31.6418 55.3394 33.1065C59.3379 34.5753 63.0045 38.356 65.0874 45.2042L64.1313 45.4952C62.1143 38.8636 58.6259 35.3789 54.9946 34.045C51.3527 32.7073 47.4731 33.4964 44.5933 35.5938L44.0054 34.7853Z"
        fill="#122138"
      />
      <path
        d="M46.6382 39.3555C50.4361 39.9539 54.0395 42.3359 55.8276 46.1943C57.6209 50.0638 57.5502 55.3299 54.1812 61.626L53.2993 61.1533C56.5599 55.0598 56.5494 50.1307 54.9204 46.6152C53.2861 43.0888 49.9839 40.8954 46.4819 40.3438L46.6382 39.3555Z"
        fill="#122138"
      />
      <path
        d="M45.2632 44.4458C47.5357 47.566 48.4073 51.7918 46.9438 55.7798C45.4764 59.7783 41.6982 63.4449 34.855 65.5278L34.564 64.5718C41.1906 62.5548 44.6727 59.0665 46.0054 55.4351C47.3419 51.7931 46.553 47.9135 44.4556 45.0337L44.8599 44.7397L45.2632 44.4458Z"
        fill="#122138"
      />
      <path
        d="M40.6929 47.0762C40.1099 50.8749 37.7381 54.4791 33.8804 56.2676C30.012 58.0609 24.7389 57.9897 18.4233 54.6211L18.894 53.7393C25.0083 57.0004 29.9454 56.9894 33.4595 55.3604C36.9841 53.7263 39.1674 50.4249 39.7046 46.9238L40.6929 47.0762Z"
        fill="#122138"
      />
      <path
        d="M15.478 35.0044C17.5 41.6308 20.9877 45.1131 24.6177 46.4458C28.2583 47.7822 32.1353 46.9934 35.0151 44.896L35.3091 45.3003L35.604 45.7036C32.484 47.976 28.2607 48.8476 24.2739 47.3843C20.2766 45.9169 16.609 42.1385 14.521 35.2954L15.478 35.0044Z"
        fill="#122138"
      />
      <path
        d="M26.3103 19.3452C23.0539 25.4546 23.0641 30.3895 24.6921 33.9038C26.3251 37.4285 29.6245 39.6138 33.1257 40.1558L33.0486 40.6499L32.9724 41.144C29.1738 40.556 25.572 38.1814 23.7849 34.3237C21.993 30.4555 22.0639 25.185 25.4275 18.8745L26.3103 19.3452Z"
        fill="#122138"
      />
      <path
        d="M45.0444 15.9282C38.4179 17.9452 34.9356 21.4336 33.603 25.0649C32.2668 28.7067 33.0555 32.5857 35.1528 35.4653L34.7485 35.7593L34.3452 36.0542C32.0727 32.9341 31.2012 28.7082 32.6645 24.7202C34.1319 20.7216 37.91 17.0542 44.7534 14.9712L45.0444 15.9282Z"
        fill="#122138"
      />
    </g>
  </svg>
);

const LeftMotor: React.FC<{ duration: number }> = ({ duration }) => (
  <svg width="106" height="92" viewBox="0 0 106 92" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M65.7041 0.5H5.00586L4.80762 0.504883C3.81899 0.556305 2.88076 0.974042 2.17676 1.68164C1.42569 2.43657 1.00238 3.46058 1 4.5293V34.0127C1.00267 35.081 1.42595 36.1047 2.17676 36.8594C2.92766 37.6141 3.94508 38.0385 5.00586 38.041H26.4688V39.999C26.4818 50.4615 30.6188 60.4925 37.9727 67.8955C45.3267 75.2985 55.2992 79.4701 65.7061 79.499C76.1264 79.4986 86.1201 75.3385 93.4893 67.9316C100.859 60.5241 105 50.4769 105 40C105 29.5231 100.859 19.4759 93.4893 12.0684C86.1197 4.6611 76.125 0.5 65.7041 0.5Z"
      fill="#F3F3F9"
      stroke="#122138"
    />
    <path
      d="M101 72.5H5C2.81472 72.5 1 74.3729 1 76.7373V87.2627C1 89.6271 2.81472 91.5 5 91.5H101C103.185 91.5 105 89.6271 105 87.2627V76.7373C105 74.3729 103.185 72.5 101 72.5Z"
      fill="#F3F3F9"
      stroke="#122138"
    />
    <g>
      <animateTransform
        attributeName="transform"
        type="rotate"
        values="360 66.19 40.25;0 66.19 40.25"
        dur={duration > 0 ? `${duration}s` : "0s"}
        repeatCount="indefinite"
      />
      <path
        d="M66.1897 14.5098C71.2806 14.5098 76.2571 16.0194 80.4901 18.8477C84.723 21.6761 88.0221 25.6961 89.9703 30.3995C91.9185 35.1029 92.4283 40.2783 91.4351 45.2714C90.4419 50.2645 87.9904 54.8509 84.3906 58.4507C80.7908 62.0505 76.2044 64.502 71.2113 65.4952C66.2182 66.4884 61.0428 65.9786 56.3394 64.0304C51.636 62.0822 47.616 58.7831 44.7876 54.5501C41.9593 50.3172 40.4497 45.3407 40.4497 40.2498C40.4484 36.8692 41.1132 33.5215 42.4063 30.3979C43.6994 27.2744 45.5954 24.4363 47.9858 22.0459C50.3763 19.6555 53.2143 17.7595 56.3378 16.4664C59.4614 15.1733 62.8091 14.5085 66.1897 14.5098Z"
        fill="#F9F9F9"
      />
      <path
        d="M66.1897 15.5098C61.2966 15.5098 56.5133 16.9607 52.4449 19.6792C48.3764 22.3977 45.2054 26.2615 43.3329 30.7822C41.4604 35.3028 40.9704 40.2772 41.925 45.0763C42.8796 49.8754 45.2359 54.2836 48.6959 57.7436C52.1558 61.2035 56.564 63.5598 61.3631 64.5144C66.1622 65.469 71.1366 64.9791 75.6573 63.1065C80.1779 61.234 84.0418 58.063 86.7602 53.9946C89.4787 49.9261 90.9297 45.1429 90.9297 40.2498C90.9217 33.6907 88.3127 27.4026 83.6747 22.7647C79.0368 18.1268 72.7487 15.5177 66.1897 15.5098ZM66.1897 14.5098C71.2806 14.5098 76.2571 16.0194 80.4901 18.8477C84.723 21.6761 88.0221 25.6961 89.9703 30.3995C91.9185 35.1029 92.4283 40.2783 91.4351 45.2714C90.4419 50.2645 87.9904 54.8509 84.3906 58.4507C80.7908 62.0505 76.2044 64.502 71.2113 65.4952C66.2182 66.4884 61.0428 65.9786 56.3394 64.0304C51.636 62.0822 47.616 58.7831 44.7876 54.5501C41.9593 50.3172 40.4497 45.3407 40.4497 40.2498C40.4484 36.8692 41.1132 33.5215 42.4063 30.3979C43.6994 27.2744 45.5954 24.4363 47.9858 22.0459C50.3763 19.6555 53.2143 17.7595 56.3378 16.4664C59.4614 15.1733 62.8091 14.5085 66.1897 14.5098Z"
        fill="#122138"
      />
      <path
        d="M66.1909 33.0703C67.6113 33.0683 69.0005 33.4878 70.1826 34.2755C71.3646 35.0632 72.2865 36.1839 72.8314 37.4957C73.3764 38.8075 73.52 40.2515 73.2441 41.6449C72.9682 43.0384 72.2852 44.3187 71.2814 45.3238C70.2777 46.3289 68.9983 47.0138 67.6053 47.2916C66.2122 47.5695 64.7681 47.4279 63.4555 46.8847C62.1429 46.3416 61.021 45.4213 60.2316 44.2404C59.4422 43.0594 59.0209 41.6708 59.0209 40.2503C59.0209 38.3478 59.7759 36.523 61.1203 35.1768C62.4646 33.8306 64.2883 33.073 66.1909 33.0703Z"
        fill="#F3F3F9"
      />
      <path
        d="M66.1909 34.0693C64.9686 34.0693 63.7737 34.4318 62.7574 35.1109C61.7411 35.7899 60.949 36.7551 60.4813 37.8844C60.0135 39.0136 59.8911 40.2562 60.1296 41.455C60.3681 42.6538 60.9566 43.755 61.8209 44.6193C62.6852 45.4836 63.7864 46.0721 64.9852 46.3106C66.184 46.5491 67.4266 46.4267 68.5558 45.9589C69.6851 45.4912 70.6503 44.6991 71.3293 43.6828C72.0084 42.6665 72.3709 41.4716 72.3709 40.2493C72.3709 39.4378 72.211 38.6342 71.9004 37.8844C71.5899 37.1346 71.1346 36.4533 70.5608 35.8794C69.9869 35.3056 69.3056 34.8503 68.5558 34.5398C67.806 34.2292 67.0024 34.0693 66.1909 34.0693ZM66.1909 33.0693C67.6113 33.0674 69.0005 33.4868 70.1826 34.2745C71.3646 35.0623 72.2865 36.1829 72.8314 37.4947C73.3764 38.8065 73.52 40.2505 73.2441 41.644C72.9682 43.0374 72.2852 44.3177 71.2814 45.3228C70.2777 46.328 68.9983 47.0128 67.6053 47.2906C66.2122 47.5685 64.7681 47.4269 63.4555 46.8837C62.1429 46.3406 61.021 45.4203 60.2316 44.2394C59.4422 43.0584 59.0209 41.6698 59.0209 40.2493C59.0209 38.3468 59.7759 36.5221 61.1203 35.1758C62.4646 33.8296 64.2883 33.072 66.1909 33.0693Z"
        fill="#122138"
      />
      <path
        d="M60.29 24.2276C56.4206 22.4344 51.1406 22.506 44.8154 25.8692L45.2842 26.752C51.4086 23.4955 56.3537 23.5059 59.8691 25.1349C63.3947 26.7687 65.5731 30.0695 66.0957 33.5743L66.5898 33.5001L67.084 33.4268C66.5166 29.6217 64.1495 26.0162 60.29 24.2276Z"
        fill="#122138"
      />
      <path
        d="M61.9941 34.7853C58.874 32.5129 54.6481 31.6418 50.6602 33.1065C46.6616 34.5753 42.995 38.356 40.9121 45.2042L41.8682 45.4952C43.8852 38.8636 47.3736 35.3789 51.0049 34.045C54.6468 32.7073 58.5265 33.4964 61.4062 35.5938L61.9941 34.7853Z"
        fill="#122138"
      />
      <path
        d="M59.3613 39.3555C55.5634 39.9539 51.96 42.3359 50.1719 46.1943C48.3786 50.0638 48.4493 55.3299 51.8184 61.626L52.7002 61.1533C49.4396 55.0598 49.4501 50.1307 51.0791 46.6152C52.7134 43.0888 56.0156 40.8954 59.5176 40.3438L59.3613 39.3555Z"
        fill="#122138"
      />
      <path
        d="M60.7363 44.4453C58.4638 47.5655 57.5922 51.7913 59.0557 55.7793C60.5231 59.7778 64.3013 63.4444 71.1445 65.5273L71.4355 64.5713C64.8089 62.5543 61.3268 59.066 59.9941 55.4346C58.6576 51.7926 59.4465 47.913 61.5439 45.0332L61.1396 44.7393L60.7363 44.4453Z"
        fill="#122138"
      />
      <path
        d="M65.3066 47.0762C65.8896 50.8749 68.2614 54.4791 72.1191 56.2676C75.9875 58.0609 81.2606 57.9897 87.5762 54.6211L87.1055 53.7393C80.9913 57.0004 76.0541 56.9894 72.54 55.3604C69.0154 53.7263 66.8321 50.4249 66.2949 46.9238L65.3066 47.0762Z"
        fill="#122138"
      />
      <path
        d="M90.5215 35.0039C88.4995 41.6303 85.0119 45.1127 81.3818 46.4453C77.7412 47.7817 73.8642 46.9929 70.9844 44.8955L70.6904 45.2998L70.3955 45.7031C73.5155 47.9756 77.7389 48.8471 81.7256 47.3838C85.7229 45.9164 89.3905 42.1381 91.4785 35.2949L90.5215 35.0039Z"
        fill="#122138"
      />
      <path
        d="M79.6892 19.3447C82.9456 25.4541 82.9354 30.389 81.3074 33.9033C79.6745 37.428 76.3751 39.6133 72.8738 40.1553L72.9509 40.6494L73.0271 41.1436C76.8258 40.5555 80.4275 38.181 82.2146 34.3232C84.0065 30.4551 83.9356 25.1846 80.572 18.874L79.6892 19.3447Z"
        fill="#122138"
      />
      <path
        d="M60.9551 15.9287C67.5816 17.9457 71.0639 21.4341 72.3965 25.0654C73.7327 28.7072 72.944 32.5862 70.8467 35.4658L71.251 35.7598L71.6543 36.0547C73.9268 32.9345 74.7984 28.7086 73.335 24.7207C71.8676 20.7221 68.0895 17.0547 61.2461 14.9717L60.9551 15.9287Z"
        fill="#122138"
      />
    </g>
  </svg>
);

Fan.displayName = "Fan";

export { Fan };
export type { FanProps };
